import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiX,
  FiUser,
  FiShield,
  FiActivity,
  FiCheck,
  FiXCircle,
  FiEye,
  FiLock,
  FiEdit,
  FiRefreshCw
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { tokenizersApi } from '../../api/smartTokenizerApi';
import { useTokenIssuer, ISSUER_STATUS, ISSUER_TYPES } from '../../contexts/TokenIssuerContext';
import { formatSectorLabel, labelTokenizerPlatformCategoryId } from '../../api/smartTokenizerMappers';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  .header-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .issuer-avatar {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    color: #fff;
    flex-shrink: 0;
  }

  .issuer-details h2 {
    color: #fff;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
  }

  .issuer-meta {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 8px;
    padding: 0.65rem;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    &.primary {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(79, 172, 254, 0.15);
  color: #4facfe;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 2rem;

  .tab {
    padding: 1rem 1.25rem;
    cursor: pointer;
    color: #a0a0a0;
    border-bottom: 2px solid transparent;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;

    &:hover {
      color: #fff;
    }

    &.active {
      color: #4facfe;
      border-bottom-color: #4facfe;
    }
  }
`;

const Content = styled.div`
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;

  .card-title {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.65rem;
    font-size: 0.9rem;
    flex-wrap: wrap;

    .label {
      color: #a0a0a0;
    }

    .value {
      color: #fff;
      font-weight: 500;
      text-align: right;
      word-break: break-all;
    }
  }
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.35rem 0.85rem;
  background: rgba(79, 172, 254, 0.1);
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 20px;
  font-size: 0.8rem;
  color: #4facfe;
`;

const ApiHint = styled.p`
  color: #a0a0a0;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const Actions = styled.div`
  padding: 1.25rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);

    &:hover {
      transform: translateY(-1px);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const categoryName = (id) => labelTokenizerPlatformCategoryId(id);

const IssuerDetailsModal = ({ isOpen, onClose, issuer, onRequestEdit }) => {
  const { updateIssuer, fetchTokenizerById, isAdmin } = useTokenIssuer();
  const [activeTab, setActiveTab] = useState('overview');
  const [detail, setDetail] = useState(issuer);
  const [refreshing, setRefreshing] = useState(false);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!issuer?.id) {
      return;
    }

    setRefreshing(true);
    try {
      const fresh = await fetchTokenizerById(issuer.id);
      if (fresh) {
        setDetail(fresh);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not refresh tokenizer from API');
    } finally {
      setRefreshing(false);
    }
  }, [fetchTokenizerById, issuer?.id]);

  useEffect(() => {
    if (!isOpen || !issuer) {
      return;
    }

    setDetail(issuer);
    setActiveTab('overview');
    loadDetail();
  }, [isOpen, issuer, loadDetail]);

  useEffect(() => {
    if (!isOpen || !isAdmin) {
      setAssignedOperators([]);
      return;
    }
    const tid = issuer?.id;
    if (!tid) {
      return;
    }
    let cancelled = false;
    setOperatorsLoading(true);
    tokenizersApi
      .assignedUsers(tid)
      .then(({ data }) => {
        if (!cancelled) {
          setAssignedOperators(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAssignedOperators([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setOperatorsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isAdmin, issuer?.id]);

  if (!isOpen || !issuer) {
    return null;
  }

  const d = detail || issuer;

  const getStatusIcon = (status) => {
    switch (status) {
      case ISSUER_STATUS.ACTIVE:
        return <FiCheck />;
      case ISSUER_STATUS.SUSPENDED:
        return <FiXCircle />;
      case ISSUER_STATUS.PENDING_VERIFICATION:
        return <FiActivity />;
      case ISSUER_STATUS.UNDER_REVIEW:
        return <FiEye />;
      case ISSUER_STATUS.REVOKED:
        return <FiLock />;
      default:
        return <FiXCircle />;
    }
  };

  const handleApprove = async () => {
    const result = await updateIssuer(d.id, { status: ISSUER_STATUS.ACTIVE });
    if (result.success) {
      toast.success('Tokenizer approved');
      setDetail(result.issuer);
    } else {
      toast.error(result.error || 'Approve failed');
    }
  };

  const handleSuspend = async () => {
    const result = await updateIssuer(d.id, { status: ISSUER_STATUS.SUSPENDED });
    if (result.success) {
      toast.success('Tokenizer suspended');
      setDetail(result.issuer);
    } else {
      toast.error(result.error || 'Suspend failed');
    }
  };

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Modal
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <div className="header-info">
            <div className="issuer-avatar">{(d.name || '?').charAt(0).toUpperCase()}</div>
            <div className="issuer-details">
              <h2>{d.name}</h2>
              <div className="issuer-meta">{ISSUER_TYPES[d.type]?.name || 'Authorized tokenizer'}</div>
              <StatusBadge>
                {getStatusIcon(d.status)}
                {String(d.status || '').replace(/_/g, ' ')}
              </StatusBadge>
            </div>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="action-btn"
              title="Refresh from GET /api/authorized-tokenizers/{id}"
              onClick={loadDetail}
              disabled={refreshing}
            >
              <FiRefreshCw />
            </button>
            {isAdmin && onRequestEdit && (
              <button type="button" className="action-btn primary" title="Edit" onClick={() => onRequestEdit(d)}>
                <FiEdit />
              </button>
            )}
            <button type="button" className="action-btn" onClick={onClose}>
              <FiX />
            </button>
          </div>
        </Header>

        <TabNavigation>
          <div
            role="button"
            tabIndex={0}
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('overview')}
          >
            <FiUser />
            Overview
          </div>
          <div
            role="button"
            tabIndex={0}
            className={`tab ${activeTab === 'sectors' ? 'active' : ''}`}
            onClick={() => setActiveTab('sectors')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('sectors')}
          >
            <FiShield />
            Sectors
          </div>
          <div
            role="button"
            tabIndex={0}
            className={`tab ${activeTab === 'api' ? 'active' : ''}`}
            onClick={() => setActiveTab('api')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('api')}
          >
            <FiActivity />
            API
          </div>
        </TabNavigation>

        <Content>
          {activeTab === 'overview' && (
            <>
              <InfoCard>
                <div className="card-title">
                  <FiUser className="icon" />
                  Registry record
                </div>
                <div className="info-item">
                  <span className="label">Tokenizer ID</span>
                  <span className="value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {d.id}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Registration number</span>
                  <span className="value">{d.registrationNumber || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Wallet address</span>
                  <span className="value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {d.walletAddress || '—'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Created</span>
                  <span className="value">{d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Last updated</span>
                  <span className="value">{d.lastUpdated ? new Date(d.lastUpdated).toLocaleString() : '—'}</span>
                </div>
              </InfoCard>
              {isAdmin && (
                <InfoCard>
                  <div className="card-title">
                    <FiUser className="icon" />
                    Assigned operator accounts
                  </div>
                  {operatorsLoading ? (
                    <ApiHint>Loading users linked to this tokenizer…</ApiHint>
                  ) : assignedOperators.length === 0 ? (
                    <ApiHint>
                      No users are assigned yet. In <strong>User management</strong>, create or edit an{' '}
                      <strong>Authorized tokenizer</strong> account and select this registry entry.
                    </ApiHint>
                  ) : (
                    assignedOperators.map((row) => (
                      <div className="info-item" key={String(row.userId ?? row.UserId)}>
                        <span className="label">{row.email ?? row.Email}</span>
                        <span className="value">
                          {row.fullName ?? row.FullName} · {String(row.status ?? row.Status)}
                        </span>
                      </div>
                    ))
                  )}
                </InfoCard>
              )}
              <ApiHint>
                Registry fields are organization, registration, wallet, sectors, and status. Operator logins are
                managed separately: each <strong>Authorized tokenizer</strong> user must be assigned to one approved
                tokenizer in User management.
              </ApiHint>
            </>
          )}

          {activeTab === 'sectors' && (
            <InfoCard>
              <div className="card-title">
                <FiShield className="icon" />
                Authorized sectors (SectorType)
              </div>
              <TagsList>
                {(d.sectors || []).map((sector) => (
                  <Tag key={sector}>{formatSectorLabel(sector)}</Tag>
                ))}
              </TagsList>
              {(!d.sectors || d.sectors.length === 0) && (
                <ApiHint>No sectors returned. Refresh from API or edit the tokenizer (Admin).</ApiHint>
              )}
              <div style={{ marginTop: '1rem' }}>
                <div className="card-title" style={{ fontSize: '0.9rem' }}>
                  Mapped asset categories (UI)
                </div>
                <TagsList>
                  {(d.allowedCategories || []).map((cat) => (
                    <Tag key={cat}>{categoryName(cat)}</Tag>
                  ))}
                </TagsList>
              </div>
            </InfoCard>
          )}

          {activeTab === 'api' && (
            <InfoCard>
              <div className="card-title">
                <FiActivity className="icon" />
                Supported operations
              </div>
              <ApiHint>
                <strong>GET</strong> <code>/api/authorized-tokenizers</code> — list all (authenticated).
                <br />
                <strong>GET</strong> <code>/api/authorized-tokenizers/{"{id}"}</code> — detail (authenticated).
                <br />
                <strong>POST</strong> <code>/api/authorized-tokenizers</code> — create (<strong>Admin</strong>).
                <br />
                <strong>PUT</strong> <code>/api/authorized-tokenizers/{"{id}"}</code> — update (<strong>Admin</strong>).
                <br />
                <strong>POST</strong> <code>/api/authorized-tokenizers/{"{id}"}/approve</code> — approve (
                <strong>Admin</strong>).
                <br />
                <strong>POST</strong> <code>/api/authorized-tokenizers/{"{id}"}/suspend</code> — suspend (
                <strong>Admin</strong>). Reactivating uses approve again.
                <br />
                <strong>GET</strong> <code>/api/authorized-tokenizers/{"{id}"}/assigned-users</code> — operator accounts
                mapped to this tokenizer (<strong>Admin</strong>).
              </ApiHint>
            </InfoCard>
          )}
        </Content>

        <Actions>
          {isAdmin && d.status === ISSUER_STATUS.PENDING_VERIFICATION && (
            <Button type="button" className="primary" onClick={handleApprove}>
              <FiCheck />
              Approve
            </Button>
          )}
          {isAdmin && d.status === ISSUER_STATUS.ACTIVE && (
            <Button type="button" className="secondary" onClick={handleSuspend}>
              <FiLock />
              Suspend
            </Button>
          )}
          {isAdmin && d.status === ISSUER_STATUS.SUSPENDED && (
            <Button type="button" className="primary" onClick={handleApprove}>
              <FiCheck />
              Approve (reactivate)
            </Button>
          )}
          <Button type="button" className="secondary" onClick={onClose}>
            Close
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default IssuerDetailsModal;
